import { Injectable } from '@angular/core';
import { BranchFunctionGroupsDaoService } from '../branch-function-groups-dao.service';
import { BranchFunctionGroup } from 'src/app/_model/branch-function-group';
import {
  DbPersistenceService,
  Query,
} from 'src/app/_services/persistence/db-persistence/db-persistence.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { BranchFunctionGroupsDbTableDefinition } from './branch-function-groups-db-table-definition';
import { LocalBranchFunctionGroupsDbTableDefinition } from './local-branch-function-groups-db-table-definition';

@Injectable()
export class BranchFunctionGroupsDbDaoService implements BranchFunctionGroupsDaoService {
  constructor(
    private logger: LoggingService,
    private dbPersistenceService: DbPersistenceService,
    private branchFunctionGroupsTableDefinition: BranchFunctionGroupsDbTableDefinition,
    private localBranchFunctionGroupsTableDefinition: LocalBranchFunctionGroupsDbTableDefinition,
  ) {}

  public async saveBranchFunctionGroups(entries: BranchFunctionGroup[]): Promise<void> {
    try {
      const db = await this.dbPersistenceService.getImsDb();
      const queries: Query[] = entries.map((entry) => ({
        statement:
          'INSERT OR REPLACE INTO ' +
          this.branchFunctionGroupsTableDefinition.tableName +
          '(id, branchid, functiongroupid, databag)' +
          ' VALUES (?,?,?,?)',
        parameters: [
          Number(entry.getImsId()),
          entry.branchid,
          entry.functiongroupid,
          entry.databag ? JSON.stringify(entry.databag) : undefined,
        ],
      }));
      await this.dbPersistenceService.executeBatch(db, queries);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error saving branch function groups to db',
        error?.message,
      );
    }
  }

  public async createBranchFunctionGroup(entry: BranchFunctionGroup): Promise<BranchFunctionGroup> {
    try {
      const db = await this.dbPersistenceService.getImsDb();
      const query: Query = {
        statement:
          'INSERT INTO ' +
          this.localBranchFunctionGroupsTableDefinition.tableName +
          '(branchid, functiongroupid, databag)' +
          ' VALUES (?,?,?)',
        parameters: [
          entry.branchid,
          entry.functiongroupid,
          entry.databag ? JSON.stringify(entry.databag) : undefined,
        ],
      };

      await this.dbPersistenceService.executeStatement(db, query);
      const res = await this.dbPersistenceService.executeQuery(db, {
        statement: 'select last_insert_rowid() as id',
        parameters: [],
      });
      if (res && res.values) {
        entry.id.secondaryId = res.values.shift()?.id;
      }
      return entry;
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error creating local branch function group mapping',
        error?.message,
      );
      return undefined;
    }
  }

  public async getBranchFunctionGroups(): Promise<BranchFunctionGroup[]> {
    const db = await this.dbPersistenceService.getImsDb();
    const entries: BranchFunctionGroup[] = [];

    // Synced entries - isolated try/catch so a problem reading the local table
    // (e.g. not migrated yet) can never wipe out the already-loaded synced ones.
    try {
      const data = await this.dbPersistenceService.executeQuery(db, {
        statement: 'SELECT * FROM ' + this.branchFunctionGroupsTableDefinition.tableName + ';',
        parameters: null,
      });
      for (let i = 0; i < data.values.length; i++) {
        const row = data.values[i];
        entries.push(
          BranchFunctionGroup.fromDto(
            {
              id: row['id'],
              branchid: row['branchid'],
              functiongroupid: row['functiongroupid'],
            },
            row['databag'] ? JSON.parse(row['databag']) : undefined,
          ),
        );
      }
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error reading synced branch function groups from db',
        error?.message,
      );
    }

    // Locally created mappings (added via the "show all" override until the
    // backend syncs the proper mapping back)
    try {
      const localData = await this.dbPersistenceService.executeQuery(db, {
        statement: 'SELECT * FROM ' + this.localBranchFunctionGroupsTableDefinition.tableName + ';',
        parameters: null,
      });
      for (let i = 0; i < localData.values.length; i++) {
        const row = localData.values[i];
        entries.push(
          BranchFunctionGroup.fromDto(
            {
              id: undefined,
              secondaryid: row['id'],
              branchid: row['branchid'],
              functiongroupid: row['functiongroupid'],
            },
            row['databag'] ? JSON.parse(row['databag']) : undefined,
          ),
        );
      }
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error reading local branch function groups from db',
        error?.message,
      );
    }

    return entries;
  }

  public async reset(): Promise<void> {
    // Unlike clear() (used during a sync), a Reset should wipe locally
    // created, not-yet-synced mappings too.
    const db = await this.dbPersistenceService.getImsDb();
    this.logger.log(
      this.constructor.name,
      'truncate table',
      this.localBranchFunctionGroupsTableDefinition.tableName,
    );
    await this.dbPersistenceService.truncateTable(
      db,
      this.localBranchFunctionGroupsTableDefinition.tableName,
    );
  }

  public async clear(): Promise<void> {
    // Only the synced table is cleared here - locally created mappings must
    // survive a resync until the backend actually confirms/sends them back.
    const db = await this.dbPersistenceService.getImsDb();
    this.logger.log(
      this.constructor.name,
      'truncate table',
      this.branchFunctionGroupsTableDefinition.tableName,
    );
    await this.dbPersistenceService.truncateTable(
      db,
      this.branchFunctionGroupsTableDefinition.tableName,
    );
  }
}
